import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Search, ClipboardCheck, Plus, Camera, ChevronRight, TrendingUp, TrendingDown, X } from 'lucide-react';

const API = window.location.origin + '/api';

const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
};

const InspectionsView = ({ reservations = [], inspectionStats = null }) => {
    const navigate = useNavigate();
    const [showNewInspection, setShowNewInspection] = useState(false);
    const [inspectionSearchQuery, setInspectionSearchQuery] = useState('');
    const [selectedReservation, setSelectedReservation] = useState('');
    const [inspectionType, setInspectionType] = useState('departure');
    const [generalNotes, setGeneralNotes] = useState('');
    const [photos, setPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [inspectionItems, setInspectionItems] = useState([
      { name: 'Écrans LED', condition: 'excellent', notes: '' },
      { name: 'Câbles et connectiques', condition: 'excellent', notes: '' },
      { name: 'Flight cases', condition: 'excellent', notes: '' },
      { name: 'Pieds et structures', condition: 'excellent', notes: '' },
      { name: 'Contrôleur vidéo', condition: 'excellent', notes: '' }
    ]);

    const handlePhotoUpload = (e) => {
      const files = Array.from(e.target.files);
      if (photos.length + files.length > 10) {
        toast.error('Maximum 10 photos autorisées');
        return;
      }
      
      files.forEach(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} est trop volumineux (max 5 Mo)`);
          return;
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
          setPhotos(prev => [...prev, {
            name: file.name,
            data: event.target.result,
            file: file
          }]);
        };
        reader.readAsDataURL(file);
      });
    };

    const removePhoto = (index) => {
      setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreateInspection = async () => {
      if (!selectedReservation) {
        toast.error('Veuillez sélectionner une réservation');
        return;
      }
      
      setUploading(true);
      try {
        // Create FormData for multipart upload
        const formData = new FormData();
        formData.append('reservation_id', selectedReservation);
        formData.append('type', inspectionType);
        formData.append('items', JSON.stringify(inspectionItems));
        formData.append('general_notes', generalNotes);
        
        // Add photos
        photos.forEach((photo, index) => {
          formData.append('photos', photo.file);
        });
        
        await axios.post(`${API}/admin/inspections`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success(`État des lieux (${inspectionType === 'entry' ? 'entrée' : 'sortie'}) créé avec succès`);
        setShowNewInspection(false);
        setPhotos([]);
        setGeneralNotes('');
        setSelectedReservation('');
        // Data will be refreshed on next view
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la création');
      } finally {
        setUploading(false);
      }
    };

    const activeReservations = reservations.filter(r => 
      r.status === 'confirmed' || r.subscription_status === 'active'
    );

    const conditionLabels = {
      excellent: { label: 'Excellent', color: 'text-green-400 bg-green-500/20' },
      good: { label: 'Bon', color: 'text-blue-400 bg-blue-500/20' },
      fair: { label: 'Correct', color: 'text-yellow-400 bg-yellow-500/20' },
      damaged: { label: 'Endommagé', color: 'text-red-400 bg-red-500/20' }
    };

    // Inspection Detail Modal
    const InspectionDetailModal = ({ inspection, onClose }) => {
      if (!inspection) return null;
      
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          <div className="relative w-full max-w-3xl mx-4 bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  inspection.type === 'entry' ? 'bg-green-500/20' : 'bg-blue-500/20'
                }`}>
                  {inspection.type === 'entry' ? 
                    <TrendingUp className="w-5 h-5 text-green-400" /> : 
                    <TrendingDown className="w-5 h-5 text-blue-400" />
                  }
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    État des lieux - {inspection.type === 'entry' ? 'Entrée' : 'Sortie'}
                  </h2>
                  <p className="text-slate-400 text-sm">
                    {inspection.customer?.firstName} {inspection.customer?.lastName} - {inspection.pack_name}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Date */}
              <div className="mb-6">
                <p className="text-sm text-slate-400">Date de l'inspection</p>
                <p className="text-white font-medium">{formatDate(inspection.created_at)}</p>
              </div>

              {/* Equipment Status */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-[#DFFF00] uppercase mb-3">État des équipements</h3>
                <div className="space-y-2">
                  {inspection.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-white">{item.name}</span>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${conditionLabels[item.condition]?.color || 'text-slate-400'}`}>
                          {conditionLabels[item.condition]?.label || item.condition}
                        </span>
                        {item.notes && (
                          <span className="text-sm text-slate-400 italic">"{item.notes}"</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* General Notes */}
              {inspection.general_notes && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-[#DFFF00] uppercase mb-3">Notes générales</h3>
                  <p className="text-slate-300 bg-slate-800/50 p-4 rounded-lg">{inspection.general_notes}</p>
                </div>
              )}

              {/* Photos */}
              {inspection.photos && inspection.photos.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#DFFF00] uppercase mb-3">
                    Photos ({inspection.photos.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {inspection.photos.map((photo, idx) => (
                      <a 
                        key={idx} 
                        href={photo.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block aspect-video rounded-lg overflow-hidden bg-slate-800 hover:ring-2 hover:ring-[#DFFF00] transition-all"
                      >
                        <img 
                          src={photo.url} 
                          alt={photo.name || `Photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* No photos message */}
              {(!inspection.photos || inspection.photos.length === 0) && (
                <div className="text-center py-6 text-slate-500">
                  <Camera className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Aucune photo pour cet état des lieux</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 bg-slate-800/50">
              <p className="text-xs text-slate-500 text-center">
                Créé par {inspection.created_by || 'Admin'} le {formatDate(inspection.created_at)}
              </p>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">États des lieux</h2>
          <Button 
            className="bg-[#DFFF00] hover:bg-[#c8e600] text-black"
            onClick={() => setShowNewInspection(!showNewInspection)}
            data-testid="new-inspection-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel état des lieux
          </Button>
        </div>

        {/* New Inspection - Selection Panel */}
        {showNewInspection && (
          <div className="bg-slate-900 rounded-xl border border-[#DFFF00]/30 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Créer un état des lieux</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm text-slate-400 block mb-2">Rechercher une réservation</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Nom, email, pack..."
                    className="w-full bg-slate-800 border-slate-700 text-white pl-10"
                    value={inspectionSearchQuery || ''}
                    onChange={(e) => setInspectionSearchQuery(e.target.value)}
                    data-testid="inspection-search-input"
                  />
                </div>
                
                {/* Filtered reservations list */}
                <div className="mt-2 max-h-48 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg">
                  {reservations
                    .filter(r => r.status === 'confirmed' || r.status === 'shipped' || r.subscription_status === 'active')
                    .filter(r => {
                      if (!inspectionSearchQuery) return true;
                      const query = inspectionSearchQuery.toLowerCase();
                      const fullName = `${r.customer?.firstName || ''} ${r.customer?.lastName || ''}`.toLowerCase();
                      const email = (r.customer?.email || '').toLowerCase();
                      const pack = (r.pack_name || '').toLowerCase();
                      const company = (r.customer?.company || '').toLowerCase();
                      return fullName.includes(query) || email.includes(query) || pack.includes(query) || company.includes(query);
                    })
                    .map(res => (
                      <button
                        key={res.id}
                        type="button"
                        onClick={() => setSelectedReservation(res.id)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-0 ${
                          selectedReservation === res.id ? 'bg-[#DFFF00]/20 border-l-2 border-l-[#DFFF00]' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-medium">
                              {res.customer?.firstName} {res.customer?.lastName}
                              {res.customer?.company && <span className="text-slate-400 ml-1">({res.customer.company})</span>}
                            </p>
                            <p className="text-xs text-slate-400">{res.customer?.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-[#DFFF00]">{res.pack_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              res.status === 'shipped' ? 'bg-green-500/20 text-green-400' :
                              res.status === 'confirmed' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-slate-600 text-slate-300'
                            }`}>
                              {res.status}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))
                  }
                  {reservations.filter(r => r.status === 'confirmed' || r.status === 'shipped' || r.subscription_status === 'active').length === 0 && (
                    <p className="text-center text-slate-500 py-4 text-sm">Aucune réservation active</p>
                  )}
                </div>
                
                {selectedReservation && (
                  <div className="mt-2 p-2 bg-[#DFFF00]/10 border border-[#DFFF00]/30 rounded-lg flex items-center justify-between">
                    <span className="text-sm text-[#DFFF00]">
                      {(() => {
                        const res = reservations.find(r => r.id === selectedReservation);
                        return res ? `${res.customer?.firstName} ${res.customer?.lastName} - ${res.pack_name}` : '';
                      })()}
                    </span>
                    <button 
                      onClick={() => setSelectedReservation('')}
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-2">Type d'état des lieux</label>
                <select 
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2"
                  value={inspectionType}
                  onChange={(e) => setInspectionType(e.target.value)}
                  data-testid="inspection-type-select"
                >
                  <option value="departure">Départ (Livraison)</option>
                  <option value="return">Retour (Restitution)</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => {
                  setShowNewInspection(false);
                  setInspectionSearchQuery('');
                }}
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-[#DFFF00] hover:bg-[#c8e600] text-black"
                disabled={!selectedReservation}
                onClick={() => {
                  if (selectedReservation) {
                    navigate(`/admin/inspections/${selectedReservation}/new?type=${inspectionType}`);
                  }
                }}
                data-testid="start-inspection-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Commencer l'état des lieux
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-semibold text-white">Sortie (Livraison)</p>
                <p className="text-sm text-slate-400">État avant mise à disposition</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-white">{inspectionStats?.entry_count || 0}</p>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-semibold text-white">Entrée (Restitution)</p>
                <p className="text-sm text-slate-400">État après retour</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-white">{inspectionStats?.exit_count || 0}</p>
          </div>

          <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#DFFF00]/20 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-[#DFFF00]" />
              </div>
              <div>
                <p className="font-semibold text-white">Total</p>
                <p className="text-sm text-slate-400">États des lieux réalisés</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-[#DFFF00]">{inspectionStats?.total || 0}</p>
          </div>
        </div>

        {/* Recent Inspections - Now Clickable */}
        {inspectionStats?.recent && inspectionStats.recent.length > 0 && (
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
            <h3 className="font-semibold text-white mb-4">États des lieux récents</h3>
            <div className="space-y-3">
              {inspectionStats.recent.map((inspection) => (
                <div 
                  key={inspection.id} 
                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 cursor-pointer transition-colors group"
                  onClick={() => setSelectedInspection(inspection)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      inspection.type === 'entry' ? 'bg-green-500/20' : 'bg-blue-500/20'
                    }`}>
                      {inspection.type === 'entry' ? 
                        <TrendingUp className="w-4 h-4 text-green-400" /> : 
                        <TrendingDown className="w-4 h-4 text-blue-400" />
                      }
                    </div>
                    <div>
                      <p className="text-sm text-white">{inspection.customer?.firstName} {inspection.customer?.lastName}</p>
                      <p className="text-xs text-slate-400">{inspection.pack_name} - {inspection.type === 'entry' ? 'Entrée' : 'Sortie'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {inspection.photos && inspection.photos.length > 0 && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Camera className="w-3 h-3" />
                        {inspection.photos.length}
                      </span>
                    )}
                    <p className="text-xs text-slate-500">{formatDate(inspection.created_at)}</p>
                    <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#DFFF00] transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Inspection Detail Modal */}
        {selectedInspection && (
          <InspectionDetailModal 
            inspection={selectedInspection} 
            onClose={() => setSelectedInspection(null)} 
          />
        )}
      </div>
    );
};

export default InspectionsView;
